import React, { useState } from "react";
import { BarChart } from "../../components/charts/Bar";

const academicYears = ["2023-24", "2022-23", "2021-22"];

const gradients = [
  "linear-gradient(135deg, #0d6efd 0%, #0dcaf0 100%)", // Blue gradient
  "linear-gradient(135deg, #6610f2 0%, #9b59b6 100%)", // Purple gradient
  "linear-gradient(135deg, #198754 0%, #20c997 100%)", // Green gradient
  "linear-gradient(135deg, #ff7e5f 0%, #feb47b 100%)", // Orange gradient
  "linear-gradient(135deg, #6f42c1 0%, #d63384 100%)", // Pink gradient
  "linear-gradient(135deg, #fd7e14 0%, #ffc107 100%)", // Gold gradient
];

// Static data for different academic years
const yearlyData = {
  "2023-24": {
    stages: [
      { name: "Inquiries", thisYear: 540, lastYear: 600 },
      { name: "Registrations", thisYear: 490, lastYear: 450 },
      { name: "Registration Fee Submission", thisYear: 480, lastYear: 440 },
      { name: "Assessment Taken", thisYear: 450, lastYear: 420 },
      { name: "Interview Taken", thisYear: 400, lastYear: 380 },
      { name: "Admission Fee Deposited", thisYear: 350, lastYear: 320 },
      { name: "Admission Accepted", thisYear: 300, lastYear: 280 },
    ],
    chartData: {
      labels: ["2019", "2020", "2021", "2022", "2023"],
      datasets: [
        {
          label: "Registrations",
          data: [500, 520, 530, 540, 550],
          backgroundColor: "rgba(75, 192, 192, 0.6)",
        },
        {
          label: "Admission Accepted",
          data: [250, 260, 270, 280, 290],
          backgroundColor: "rgba(255, 158, 102, 0.6)",
        },
      ],
    },
  },
  "2022-23": {
    stages: [
      { name: "Inquiries", thisYear: 600, lastYear: 580 },
      { name: "Registrations", thisYear: 550, lastYear: 520 },
      { name: "Registration Fee Submission", thisYear: 530, lastYear: 500 },
      { name: "Assessment Taken", thisYear: 500, lastYear: 480 },
      { name: "Interview Taken", thisYear: 460, lastYear: 440 },
      { name: "Admission Fee Deposited", thisYear: 410, lastYear: 390 },
      { name: "Admission Accepted", thisYear: 360, lastYear: 340 },
    ],
    chartData: {
      labels: ["2018", "2019", "2020", "2021", "2022"],
      datasets: [
        {
          label: "Inquiries",
          data: [480, 490, 500, 510, 520],
          backgroundColor: "rgba(75, 192, 192, 0.6)",
        },
        {
          label: "Admissions",
          data: [230, 240, 250, 260, 270],
          backgroundColor: "rgba(255, 158, 102, 0.6)",
        },
      ],
    },
  },
  "2021-22": {
    stages: [
      { name: "Inquiries", thisYear: 580, lastYear: 560 },
      { name: "Registrations", thisYear: 520, lastYear: 500 },
      { name: "Registration Fee Submission", thisYear: 500, lastYear: 480 },
      { name: "Assessment Taken", thisYear: 470, lastYear: 450 },
      { name: "Interview Taken", thisYear: 430, lastYear: 410 },
      { name: "Admission Fee Deposited", thisYear: 380, lastYear: 360 },
      { name: "Admission Accepted", thisYear: 330, lastYear: 310 },
    ],
    chartData: {
      labels: ["2017", "2018", "2019", "2020", "2021"],
      datasets: [
        {
          label: "Inquiries",
          data: [460, 470, 480, 490, 500],
          backgroundColor: "rgba(75, 192, 192, 0.6)",
        },
        {
          label: "Admissions",
          data: [210, 220, 230, 240, 250],
          backgroundColor: "rgba(255, 158, 102, 0.6)",
        },
      ],
    },
  },
};

const icons = [
  "fa-question-circle",
  "fa-clipboard",
  "fa-money-bill",
  "fa-pencil-alt",
  "fa-comments",
  "fa-wallet",
  "fa-check-circle",
];

const colors = [
  "#17a2b8", // Cyan Blue
  "#0056b3", // Darker Blue
  "#007bff", // Primary Blue
  "#6610f2", // Indigo
  "#20c997", // Teal
  "#343a40", // Dark Grey
  "#ffc107", // Yellow
];

const getTextColor = (bgColor) => {
  const lightColors = ["#fd7e14", "#e83e8c"];
  return lightColors.includes(bgColor) ? "black" : "white";
};

const chartOptions = {
  responsive: true,
  maintainAspectRatio: true,
  aspectRatio: 3,
  plugins: {
    legend: { position: "top" },
    title: {
      display: true,
      text: "Yearly Registrations and Admission Accepted",
      font: { size: 18 },
    },
  },
  scales: {
    x: { title: { display: true, text: "x" } },
    y: { title: { display: true, text: "Count" }, beginAtZero: true },
  },
};

function InquiryToEnrollmentInsights() {
  const [selectedAY, setSelectedAY] = useState(academicYears[0]);

  // Get data for selected academic year
  const selectedData = yearlyData[selectedAY];
  const updatedStages = selectedData.stages.slice(1);
  const updatedIcons = icons.slice(1);
  const updatedColors = colors.slice(1);

  // Calculate transition rates between consecutive stages
  const transitionData = updatedStages
    .slice(0, -1)
    .map((currentStage, index) => {
      const nextStage = updatedStages[index + 1];
      const thisYearPercentage =
        (nextStage.thisYear / currentStage.thisYear) * 100;
      const lastYearPercentage =
        (nextStage.lastYear / currentStage.lastYear) * 100;
      const trend = thisYearPercentage > lastYearPercentage ? "up" : "down";
      return {
        from: currentStage.name,
        to: nextStage.name,
        thisYearPercentage: thisYearPercentage.toFixed(1),
        lastYearPercentage: lastYearPercentage.toFixed(1),
        trend,
      };
    });

  return (
    <div>
      {/* Dashboard Header with AY Dropdown */}
      <div className="mb-3 d-flex align-items-end">
        <div>
          <h3>Registration to Enrollment Insights</h3>
          <p className="text-body-tertiary mb-0">
            Track the journey from registrations to enrollment.
          </p>
        </div>
        <div className="ms-auto" style={{ minWidth: "150px" }}>
          <label className="form-label">Select AY</label>
          <select
            className="form-select"
            value={selectedAY}
            onChange={(e) => setSelectedAY(e.target.value)}
          >
            {academicYears.map((ay) => (
              <option key={ay} value={ay}>
                {ay}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Single Row: 6 Tiles */}
      <div className="row row-cols-1 row-cols-sm-2 row-cols-md-2 row-cols-lg-3 row-cols-xl-6 g-3 mb-3">
        {updatedStages.map((stage, index) => (
          <div key={index} className="col">
            <div
              className="infographic-card card"
              style={{
                backgroundColor: updatedColors[index],
                color: getTextColor(updatedColors[index]),
              }}
            >
              <div className="card-body px-2 py-1 text-center">
                <i className={`fas ${updatedIcons[index]} mb-1`}></i>
                <p className="small mb-1">{stage.name}</p>
                <h6>{stage.thisYear}</h6>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Stage Progression Rates */}
      <div className="row g-3 mb-3" style={{ marginTop: "-2rem" }}>
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title">Stage Progression Rates</h5>
              <div className="row row-cols-1 row-cols-md-3 row-cols-lg-5 g-3">
                {transitionData.map((transition, index) => (
                  <div key={index} className="col">
                    <div className="transition-box text-center">
                      <h6>{transition.to}</h6>
                      <div className="percentage">
                        <span title="This Year">
                          {transition.thisYearPercentage}%
                        </span>
                      </div>
                      <i
                        className={`fas fa-arrow-${transition.trend} fa-2x`}
                        style={{
                          color:
                            transition.trend === "up" ? "#28a745" : "#dc3545",
                        }}
                      ></i>
                      <div className="percentage">
                        <span title="SPLY">
                          {transition.lastYearPercentage}%
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bar Chart */}
      <div className="row g-3">
        <div className="col-12">
          <div className="card">
            <div className="card-body px-3 py-4">
              <h5 className="mb-3">
                Yearly Registrations and Admission Accepted
              </h5>
              <div style={{ height: "300px" }}>
                <BarChart
                  chartType="inquiries"
                  chartData={selectedData.chartData}
                  options={chartOptions}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default InquiryToEnrollmentInsights;
